/// Aqui é o componente de gerenciamento de hábitos
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Typography,
  Switch,
  Tooltip,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Grid,
  Card
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getCurrentDateTime } from '../utils/dateUtils';
import { v4 as uuidv4 } from 'uuid';
import { doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { getContrastText } from '../utils/colorUtils';
import { usePlan } from '../contexts/PlanContext';
import { hasReachedLimit } from '../config/planLimits';
import PlanComparisonDialog from './shared/PlanComparisonDialog';
import PlanInfoBanner from './shared/PlanInfoBanner';

const WEEKDAYS = [
  { id: 1, name: 'Segunda', short: 'Seg' },
  { id: 2, name: 'Terça', short: 'Ter' },
  { id: 3, name: 'Quarta', short: 'Qua' },
  { id: 4, name: 'Quinta', short: 'Qui' },
  { id: 5, name: 'Sexta', short: 'Sex' },
  { id: 6, name: 'Sábado', short: 'Sáb' },
  { id: 7, name: 'Domingo', short: 'Dom' }
];

const PILLARS = [
  { id: 'vitalidade', label: 'Vitalidade (Combustível)' },
  { id: 'mentalidade', label: 'Mentalidade (Controle)' },
  { id: 'prosperidade', label: 'Prosperidade (Alicerce)' }
];

const DEFAULT_PILLAR_COLORS = {
  vitalidade: '#2E7D32',
  mentalidade: '#F9A825',
  prosperidade: '#E65100'
};

function getPillarLabel(pillarId) {
  const normalizedId = pillarId === 'expansao' ? 'mentalidade' : (pillarId || 'vitalidade');
  const p = PILLARS.find((x) => x.id === normalizedId);
  const full = p ? p.label : PILLARS[0].label;
  return full.includes(' (') ? full.split(' (')[0] : full;
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function calculateStats(habit) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // Calcula quantos dias do mês atual o hábito está programado
  let programmedDays = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentYear, currentMonth, day);
    const weekDay = date.getDay();
    const adjustedWeekDay = weekDay === 0 ? 7 : weekDay;
    
    if (!habit.weekdays?.length || habit.weekdays.includes(adjustedWeekDay)) {
      programmedDays++;
    }
  }

  // Calcula quantos dias do mês atual o hábito foi realizado
  const completedThisMonth = habit.completedDates?.filter(date => {
    try {
      // Normaliza a data removendo a parte do horário
      const dateStr = date.split(' - ')[0];
      const [day, month, year] = dateStr.split('/').map(Number);
      const completedDate = new Date(year, month - 1, day);
      
      return completedDate.getMonth() === currentMonth &&
             completedDate.getFullYear() === currentYear;
    } catch (error) {
      console.error('Erro ao processar data:', date, error);
      return false;
    }
  }).length || 0;

  return { total: programmedDays, thisMonth: completedThisMonth };
}

function formatWeekdays(weekdays) {
  if (!weekdays || weekdays.length === 0) return 'Todos os dias';
  
  // Verifica se TODOS os dias da semana estão selecionados
  const allDays = [1, 2, 3, 4, 5, 6, 7];
  const hasAllDays = allDays.every(day => weekdays.includes(day));
  if (hasAllDays) return 'Todos os dias';
  
  // Verifica se são dias úteis (segunda a sexta)
  if (weekdays.length === 5 && 
      weekdays.includes(1) && 
      weekdays.includes(2) && 
      weekdays.includes(3) && 
      weekdays.includes(4) && 
      weekdays.includes(5)) return 'Seg a Sex';
  
  return weekdays
    .sort((a, b) => {
      // Reordenar para que domingo (7) venha primeiro
      const adjustA = a === 7 ? 0 : a;
      const adjustB = b === 7 ? 0 : b;
      return adjustA - adjustB;
    })
    .map(day => WEEKDAYS.find(d => d.id === day)?.short)
    .join(', ');
}

function StatCard({ icon, title, value, color = 'primary', customColors }) {
  return (
    <Card sx={{ 
      p: 2, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      borderRadius: 2,
      boxShadow: 2,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    }}>
      <Box sx={{ 
        color: customColors?.primary || color, 
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {React.cloneElement(icon, { 
          sx: { fontSize: { xs: 32, sm: 40 } }
        })}
      </Box>
      <Typography 
        variant="h6" 
        component="div" 
        gutterBottom 
        sx={{ 
          fontSize: { xs: '0.875rem', sm: '1rem' },
          fontWeight: 'bold',
          lineHeight: 1.2,
          mb: 1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="h6" 
        sx={{ 
          color: customColors?.primary || 'primary.main',
          fontSize: { xs: '1rem', sm: '1.25rem' },
          fontWeight: 'bold'
        }}
      >
        {value}
      </Typography>
    </Card>
  );
}



const HabitManagement = forwardRef(({ habits = [], onDeleteHabit, onUpdateHabit, onReorderHabits, onAddHabit, onDeleteAllHabits, customColors }, ref) => {
  const { currentUser } = useAuth();
  const [editingHabit, setEditingHabit] = useState(null);
  const [newName, setNewName] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [editActive, setEditActive] = useState(true);
  const [selectedPillar, setSelectedPillar] = useState('vitalidade');
  const [startDate, setStartDate] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [isAddingHabit, setIsAddingHabit] = useState(false);
  const [isDeleteAllConfirmationOpen, setIsDeleteAllConfirmationOpen] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userPlan, permissions } = usePlan();

  const getPillarColor = (pillarId) => {
    const id = (pillarId === 'expansao' ? 'mentalidade' : (pillarId || 'vitalidade'));
    return customColors?.pillars?.[id] || DEFAULT_PILLAR_COLORS[id] || DEFAULT_PILLAR_COLORS.vitalidade;
  };

  // Garante que habits seja um array e então ordena
  const sortedHabits = Array.isArray(habits) ? [...habits].sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : 0;
    const orderB = typeof b.order === 'number' ? b.order : 0;
    return orderA - orderB;
  }) : [];

  const handleMoveHabit = (currentIndex, direction) => {
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < sortedHabits.length) {
      console.log('Moving habit:', { currentIndex, newIndex, direction });
      onReorderHabits(currentIndex, newIndex);
    }
  };

  const handleEditClick = (habit) => {
    setEditingHabit(habit);
    setNewName(habit.name);
    setSelectedDays(habit.weekdays || []);
    setEditActive(habit.active !== false);
    setSelectedPillar(habit.pillar || 'vitalidade');
    // Usa a data de início existente ou deixa em branco para o usuário definir
    setStartDate(habit.startDate || '');
  };

  const handleClose = () => {
    setEditingHabit(null);
    setNewName('');
    setSelectedDays([]);
    setEditActive(true);
    setSelectedPillar('vitalidade');
    setStartDate('');
  };

  const handleSave = async () => {
    const trimmedName = newName.trim();
    if (trimmedName && editingHabit) {
      // Verifica se já existe um hábito com este nome (exceto o próprio hábito sendo editado)
      const isDuplicate = habits.some(h => 
        h.id !== editingHabit.id && 
        String(h.name || '').toLowerCase() === trimmedName.toLowerCase()
      );

      // Verifica se o nome é muito curto (menos de 3 caracteres)
      if (trimmedName.length < 3) {
        alert('O nome do hábito deve ter pelo menos 3 caracteres.');
        return;
      }

      // Verifica se é um nome duplicado
      if (isDuplicate) {
        alert('Já existe um hábito com este nome.');
        return;
      }

      try {
        if (editingHabit) {
          // Chama onUpdateHabit para atualizar o hábito
          onUpdateHabit(editingHabit.id, {
            name: trimmedName,
            weekdays: selectedDays,
            active: selectedDays.length > 0 ? editActive : false,
            updatedAt: getCurrentDateTime(),
            pillar: selectedPillar || editingHabit.pillar || 'vitalidade',
            // Se o usuário definiu uma data, usamos ela; caso contrário preservamos a existente
            startDate: startDate || editingHabit.startDate || null
          });
          handleClose();
        }
      } catch (error) {
        console.error('Erro ao salvar hábito:', error);
        alert('Erro ao salvar hábito. Por favor, tente novamente.');
      }
    }
  };

  const handleDayToggle = (dayId) => {
    setSelectedDays(prev => 
      prev.includes(dayId)
        ? prev.filter(d => d !== dayId)
        : [...prev, dayId].sort()
    );
  };

  const handleDeleteClick = (habit) => {
    setDeleteConfirmation(habit);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmation) {
      onDeleteHabit(deleteConfirmation.id);
      setDeleteConfirmation(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation(null);
  };

  const handleAddClose = () => {
    setIsAddingHabit(false);
    setNewName('');
    setSelectedDays([]);
    setSelectedPillar('vitalidade');
    setStartDate('');
  };

  const handleAddClick = () => {
    // Verifica se o usuário está no plano básico e já atingiu o limite
    if (hasReachedLimit(userPlan, 'maxHabits', habits.length)) {
      setShowPlanDialog(true);
      return;
    }
    setIsAddingHabit(true);
    setNewName('');
    setSelectedDays(WEEKDAYS.map(day => day.id));
    setSelectedPillar('vitalidade');
    // Data padrão: hoje
    setStartDate(new Date().toISOString().split('T')[0]);
  };

  // Expõe funções através do ref para uso externo (ex: abrir modal de novo/editar hábito)
  useImperativeHandle(ref, () => ({
    handleAddClick: () => {
      // Verifica se o usuário está no plano básico e já atingiu o limite
      if (hasReachedLimit(userPlan, 'maxHabits', habits.length)) {
        setShowPlanDialog(true);
        return;
      }
      setIsAddingHabit(true);
      setNewName('');
      setSelectedDays(WEEKDAYS.map(day => day.id));
      setSelectedPillar('vitalidade');
    },
    handleEditHabit: (habit) => {
      if (habit) {
        handleEditClick(habit);
      }
    }
  }));

  const handleAddSave = async () => {
    const trimmedName = newName.trim();
    if (trimmedName) {
      // Verifica se o usuário está no plano básico e já atingiu o limite
      if (hasReachedLimit(userPlan, 'maxHabits', habits.length)) {
        setShowPlanDialog(true);
        setIsAddingHabit(false);
        return;
      }

      // Verifica se já existe um hábito com este nome
      const isDuplicate = habits.some(h => 
        String(h.name || '').toLowerCase() === trimmedName.toLowerCase()
      );

      // Verifica se o nome é muito curto (menos de 3 caracteres)
      if (trimmedName.length < 3) {
        alert('O nome do hábito deve ter pelo menos 3 caracteres.');
        return;
      }

      // Verifica se é um nome duplicado
      if (isDuplicate) {
        alert('Já existe um hábito com este nome.');
        return;
      }

      // Criando novo hábito
      const newHabitWithId = {
        id: uuidv4(),
        name: trimmedName,
        weekdays: selectedDays,
        active: selectedDays.length > 0,
        createdAt: getCurrentDateTime(),
        updatedAt: getCurrentDateTime(),
        completedDates: [],
        order: habits.length > 0 ? habits[habits.length - 1].order + 1 : 0,
        pillar: selectedPillar || 'vitalidade',
        // Data de início: a escolhida pelo usuário ou hoje como fallback
        startDate: startDate || new Date().toISOString().split('T')[0]
      };

      try {
        await onAddHabit(newHabitWithId);
        handleAddClose();
      } catch (error) {
        console.error('Erro ao adicionar hábito:', error);
        alert('Erro ao adicionar hábito. Por favor, tente novamente.');
      }
    }
  };

  const handleDeleteAllClick = () => {
    setIsDeleteAllConfirmationOpen(true);
  };

  const handleDeleteAllConfirm = () => {
    if (onDeleteAllHabits) {
      onDeleteAllHabits();
    } else {
      // Fallback para o método antigo se onDeleteAllHabits não estiver disponível
      const habitsToDelete = [...habits];
      habitsToDelete.forEach(habit => {
        onDeleteHabit(habit.id);
      });
    }
    setIsDeleteAllConfirmationOpen(false);
  };

  const handleDeleteAllCancel = () => {
    setIsDeleteAllConfirmationOpen(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Título da seção */}
      <Box sx={{ mb: 3 }}>
        
        
        {/* Banner informativo do plano */}
        <PlanInfoBanner
          feature="maxHabits"
          featureName="hábitos"
          featureNameSingular="hábito"
          currentCount={habits.length}
          customColors={customColors}
          onUpgradeClick={() => setShowPlanDialog(true)}
        />
        
        <Typography variant="body1" color="text.secondary">
          Crie, edite e organize seus hábitos diários. Defina em quais dias da semana cada hábito deve ser realizado.
        </Typography>
      </Box>

      {/* Cards de Estatísticas */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={4}>
            <StatCard
              icon={<AssignmentIcon />}
              title="Total de Hábitos"
              value={habits.length}
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <StatCard
              icon={<CheckCircleIcon />}
              title="Hábitos Ativos"
              value={habits.filter(habit => habit.active).length}
              color="success"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={4}>
            <StatCard
              icon={<BlockIcon />}
              title="Hábitos Inativos"
              value={habits.filter(habit => !habit.active).length}
              color="error"
              customColors={customColors}
            />
          </Grid>
        </Grid>
      </Box>

      <List>
        {sortedHabits.map((habit, index) => {
          const stats = calculateStats(habit);
          const pillarColor = getPillarColor(habit.pillar);
          const pillarLabel = getPillarLabel(habit.pillar || 'vitalidade');
          return (
            <Paper
              key={habit.id}
              elevation={1}
              sx={{
                mb: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                borderLeft: `4px solid ${pillarColor}`
              }}
            >
              <ListItem
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  gap: { xs: 2, sm: 1 },
                  py: 2
                }}
              >
                <Box sx={{ 
                  flex: 1, 
                  minWidth: 0, 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  width: '100%'
                }}>
                  {/* Clique no box do hábito abre a edição */}
                  <Box
                    onClick={() => handleEditClick(habit)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'center', sm: 'flex-start' },
                      gap: 1.5,
                      cursor: 'pointer',
                      '&:hover .habit-name': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                  <Typography
                    variant="h6"
                    sx={{
                      '&.habit-name': {},
                      fontSize: '1rem',
                      color: 'primary.main',
                      fontWeight: 500,
                      minWidth: 'fit-content',
                      textAlign: { xs: 'center', sm: 'left' }
                    }}
                    className="habit-name"
                  >
                    {habit.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={pillarLabel}
                    sx={{
                      bgcolor: pillarColor,
                      color: getContrastText(pillarColor),
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}
                  />
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    width: { xs: '100%', sm: 'auto' }
                  }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ 
                        minWidth: 'fit-content',
                        textAlign: { xs: 'center', sm: 'left' }
                      }}
                    >
                      Frequência:
                    </Typography>
                    <Chip
                      size="small"
                      icon={<ScheduleIcon />}
                      label={formatWeekdays(habit.weekdays)}
                      sx={{ bgcolor: 'background.default' }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>•</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 'fit-content' }}
                    >
                      Início: {habit.startDate
                        ? (() => {
                            const [y, m, d] = habit.startDate.split('-');
                            return d && m && y ? `${d}/${m}/${y}` : 'A definir';
                          })()
                        : 'A definir'}
                    </Typography>
                  </Box>
                  </Box>
                </Box>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 40px)',
                  gap: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Ativar/Desativar">
                      <Switch
                        checked={habit.active}
                        onChange={() => onUpdateHabit(habit.id, { active: !habit.active })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: customColors?.primary
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: customColors?.primary
                          }
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Editar">
                      <IconButton
                        onClick={() => handleEditClick(habit)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Excluir">
                      <IconButton
                        onClick={() => handleDeleteClick(habit)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {index > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Reordenar para cima">
                        <IconButton
                          onClick={() => handleMoveHabit(index, 'up')}
                          size="small"
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {index < sortedHabits.length - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Reordenar para baixo">
                        <IconButton
                          onClick={() => handleMoveHabit(index, 'down')}
                          size="small"
                        >
                          <ArrowDownwardIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </ListItem>
            </Paper>
          );
        })}
      </List>

      {/* Edit Dialog - modelo igual ao do Calendário */}
      <Dialog open={!!editingHabit} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Editar Hábito</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do hábito"
            type="text"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <FormGroup sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editActive}
                  onChange={(e) => setEditActive(e.target.checked)}
                  size="small"
                />
              }
              label="Hábito ativo"
            />
          </FormGroup>
          <TextField
            margin="dense"
            label="Começou em"
            type="date"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Pilar do hábito
            </Typography>
            <TextField
              select
              fullWidth
              value={selectedPillar}
              onChange={(e) => setSelectedPillar(e.target.value)}
              SelectProps={{ native: true }}
            >
              {PILLARS.map(p => (
                <option key={p.id} value={p.id}>
                  {getPillarLabel(p.id)}
                </option>
              ))}
            </TextField>
          </Box>
          <FormGroup sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Dias da semana
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {WEEKDAYS.map((day) => (
                <FormControlLabel
                  key={day.id}
                  control={
                    <Switch
                      checked={selectedDays.includes(day.id)}
                      onChange={() => handleDayToggle(day.id)}
                    />
                  }
                  label={day.name}
                />
              ))}
            </Box>
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            color="error"
            onClick={() => {
              if (editingHabit) {
                handleDeleteClick(editingHabit);
                handleClose();
              }
            }}
          >
            Excluir
          </Button>
          <Box>
            <Button onClick={handleClose} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button onClick={handleSave} variant="contained">
              Salvar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={isAddingHabit} onClose={handleAddClose}>
        <DialogTitle>Novo Hábito</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do hábito"
            type="text"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Pilar do hábito
            </Typography>
            <TextField
              select
              fullWidth
              value={selectedPillar}
              onChange={(e) => setSelectedPillar(e.target.value)}
              SelectProps={{ native: true }}
            >
              {PILLARS.map(p => (
                <option key={p.id} value={p.id}>
                  {getPillarLabel(p.id)}
                </option>
              ))}
            </TextField>
          </Box>
          <TextField
            margin="dense"
            label="Começou em"
            type="date"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
          <FormGroup sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Dias da semana
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {WEEKDAYS.map((day) => (
                <FormControlLabel
                  key={day.id}
                  control={
                    <Switch
                      checked={selectedDays.includes(day.id)}
                      onChange={() => handleDayToggle(day.id)}
                    />
                  }
                  label={day.name}
                />
              ))}
            </Box>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose}>Cancelar</Button>
          <Button onClick={handleAddSave} variant="contained">
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmation}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o hábito "{deleteConfirmation?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={isDeleteAllConfirmationOpen}
        onClose={() => setIsDeleteAllConfirmationOpen(false)}
      >
        <DialogTitle>Confirmar exclusão de todos os hábitos</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover todos os hábitos? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteAllConfirmationOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              onDeleteAllHabits();
              setIsDeleteAllConfirmationOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Excluir Todos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Comparison Dialog */}
      <PlanComparisonDialog 
        open={showPlanDialog}
        onClose={() => setShowPlanDialog(false)}
        customColors={customColors}
      />
    </Box>
  );
});

export default HabitManagement; 